import { Link } from "react-router-dom";
import { Phone, MessageSquare, Zap, TrendingUp, PiggyBank, Shield } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import ZipHero from "@/components/ZipHero";
import HowItWorks from "@/components/HowItWorks";
import CustomerCards from "@/components/CustomerCards";
import EssentialServices from "@/components/EssentialServices";
import JeffreyIntro from "@/components/JeffreyIntro";
import AboutJeffrey from "@/components/AboutJeffrey";
import BrandStrip from "@/components/BrandStrip";
import MealsCounter from "@/components/MealsCounter";
import OpportunityTeaser from "@/components/OpportunityTeaser";
import GivingBack from "@/components/GivingBack";
import FAQ from "@/components/FAQ";
import WhyOverpay from "@/components/WhyOverpay";
import Disclosure from "@/components/Disclosure";
import { BUSINESS_PHONE, BUSINESS_PHONE_FORMATTED } from "@/lib/constants";
import { useT } from "@/lib/i18n";

export default function HomePage() {
    const { t } = useT();
    return (
        <div className="min-h-screen" data-testid="home-page">
            <Navbar />

            {/* Hero */}
            <section className="relative">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="hero-glow -top-24 -left-24" />
                    <div className="hero-glow top-10 right-0" style={{ animationDelay: "1.6s" }} />
                    <div className="absolute inset-0 spark-pattern opacity-30" />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-20">
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <span className="chip reveal">{t("hero_chip")}</span>
                            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white reveal d1">
                                {t("hero_h1_a")} <span className="accent-gradient-text">{t("hero_h1_b")}</span>
                                <br className="hidden sm:block" />
                                {t("hero_h1_c")}
                            </h1>
                            <p className="mt-5 text-lg text-slate-300 max-w-xl leading-relaxed reveal d2">
                                {t("hero_sub")}
                            </p>

                            <ZipHero />

                            <div className="mt-4 flex flex-wrap gap-3 reveal d3">
                                <a href={`tel:${BUSINESS_PHONE}`} className="btn-ghost" data-testid="hero-call-cta">
                                    <Phone className="w-4 h-4" /> {t("cta_call_me")} {BUSINESS_PHONE_FORMATTED}
                                </a>
                                <a
                                    href={`sms:${BUSINESS_PHONE}?&body=Text me my electricity options`}
                                    className="btn-ghost"
                                    data-testid="hero-text-cta"
                                >
                                    <MessageSquare className="w-4 h-4" /> {t("cta_text_full")}
                                </a>
                            </div>

                            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md reveal d4">
                                {[
                                    { Icon: TrendingUp, label: t("trust_know") },
                                    { Icon: PiggyBank, label: t("trust_keep") },
                                    { Icon: Shield, label: t("trust_pressure") },
                                ].map(({ Icon, label }) => (
                                    <div key={label} className="glass rounded-xl p-3 flex items-center gap-2">
                                        <Icon className="w-4 h-4 text-emerald-300" />
                                        <span className="text-xs text-slate-200">{label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Hero visual */}
                        <div className="lg:col-span-5 reveal d2">
                            <div className="relative">
                                <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                                    <img
                                        src="https://images.unsplash.com/photo-1758687126877-b37052a20a4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGZhbWlseSUyMGhvbWUlMjBlbmVyZ3klMjBzYXZpbmdzfGVufDB8fHx8MTc5MDk5OTExM3ww&ixlib=rb-4.1.0&q=85"
                                        alt="Family saving money on electricity"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="absolute -left-4 -bottom-4 glass rounded-2xl p-4 w-56 shadow-xl">
                                    <div className="flex items-center gap-2">
                                        <Zap className="w-4 h-4 text-emerald-300" />
                                        <span className="text-xs mono text-slate-400">Avg call-back</span>
                                    </div>
                                    <div className="mt-1 text-2xl font-extrabold text-white">under 15 min</div>
                                    <div className="mt-1 text-xs text-slate-400">
                                        A real person, not a bot.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trust / value */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
                <div className="grid gap-5 lg:grid-cols-3">
                    <div className="card-surface p-6">
                        <span className="chip">Compare</span>
                        <h3 className="mt-3 text-xl font-semibold text-white">See what's available</h3>
                        <p className="text-sm text-slate-400 mt-2">
                            We help you review plans and providers in your service area side-by-side.
                        </p>
                    </div>
                    <div className="card-surface p-6">
                        <span className="chip">Understand</span>
                        <h3 className="mt-3 text-xl font-semibold text-white">Rates & contract terms</h3>
                        <p className="text-sm text-slate-400 mt-2">
                            We explain fixed vs. variable, length, early-exit, and base fees in plain English.
                        </p>
                    </div>
                    <div className="card-surface p-6">
                        <span className="chip">Decide</span>
                        <h3 className="mt-3 text-xl font-semibold text-white">What works for YOU</h3>
                        <p className="text-sm text-slate-400 mt-2">
                            You stay in control. No pressure, no auto-enrollment, ever.
                        </p>
                    </div>
                </div>
            </section>

            <HowItWorks />
            <AboutJeffrey />
            <BrandStrip />
            <EssentialServices />
            <WhyOverpay />
            <CustomerCards />
            <GivingBack />
            <MealsCounter />
            <JeffreyIntro />
            <OpportunityTeaser />
            <FAQ />

            {/* CTA band */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
                <div className="card-surface p-8 sm:p-10 flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
                    <div>
                        <span className="chip">Ready?</span>
                        <h3 className="mt-3 text-2xl sm:text-3xl font-extrabold text-white">
                            Text us for your options.
                        </h3>
                        <p className="mt-2 text-slate-400 max-w-xl">
                            60 seconds to request help. We'll follow up only through the contact
                            method you choose.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/get-started" className="btn-primary" data-testid="cta-get-started-btn">
                            Check my options
                        </Link>
                        <a href={`sms:${BUSINESS_PHONE}?&body=Text me for your options`} className="btn-ghost" data-testid="cta-text-btn">
                            <MessageSquare className="w-4 h-4" /> Text {BUSINESS_PHONE_FORMATTED}
                        </a>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
                <Disclosure />
            </section>

            <Footer />
            <StickyMobileCTA />
        </div>
    );
}
